'use client';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { Skeleton, Stack, Theme, Typography } from '@mui/material';
import { ReactElement } from 'react';

/**
 * What stands in for a permission this organisation does not hold. The same dash every other field
 * on the details card uses, and the same one the check cards and the KYC table use.
 */
const MISSING_VALUE = '-';

/**
 * The customer's standing permission: whether it is live right now, and the one date that says so.
 *
 * Restored to the Details card in review after it was noticed missing from the redesign. It is not
 * new work: it exists on the live page today, and dropping it silently would have removed a record
 * a client relies on.
 *
 * **One permission, no history.** This surface is a single organisation context and the page renders
 * exactly one permission per customer. Permission history is a separate page. That is why there is
 * no name line: the "Permission" caption is the label, because there is nothing to tell apart.
 *
 * **Considered and overturned: a labelled presentation.** PR 2043 (A7, verifime-adr#264) briefly gave
 * this field a name line, reasoning that rendering exactly one permission says nothing about whether
 * the client needs to be told *which* permission it is. Charles overturned that in the PR 2043 review
 * on 9 August 2026: the presentation above is deliberate, not a gap, so it stands without a name
 * line and this paragraph's reasoning is not superseded after all.
 *
 * Exactly one date, chosen by the status, rather than two optional ones. Neither both-set nor
 * both-absent can compile, so the "either a granted on or a revoked on" rule is enforced at every
 * call site instead of being a convention the component hopes callers follow.
 *
 * The discriminant is the status string rather than an `isActive` boolean because this repo builds
 * with `strictNullChecks: false` (`packages/config-typescript/base.json`), under which TypeScript
 * does not narrow a boolean-literal union: the false branch keeps the full union and the revoked
 * date fails to resolve. A string discriminant narrows correctly under the same settings, and its
 * values double as the two words the field renders.
 */
export type TPermissionBlockProps =
  | {
      status: 'active';
      /** When the customer granted it, already formatted. */
      grantedOn: string;
    }
  | {
      status: 'inactive';
      /** When it was revoked, already formatted. This replaces the grant date rather than joining it. */
      revokedOn: string;
    }
  | {
      /**
       * This organisation holds no permission record for this customer.
       *
       * **Renders the field with a dash rather than removing it**, which is the same convention
       * every other field on the details card follows: an absent name or date of birth shows `-`,
       * so an absent permission showing nothing at all made one field behave unlike its five
       * neighbours. The dash is also the more useful answer, since a field that disappears leaves a
       * client wondering whether the card is broken or the record is empty.
       *
       * An earlier version omitted the field. That was defended on the grounds that a blank row
       * would claim we hold an empty record, which is true of a *blank* row and not of a dash: the
       * dash is this surface's standing way of saying "not available", used by the check cards'
       * timestamp, `EvidenceProvenance` and the KYC table.
       */
      status: 'none';
    }
  | {
      /**
       * The permission is still being fetched.
       *
       * **Not the same as omitting the block.** An absent block says this organisation holds no
       * permission for this customer, which is a fact about their relationship and one a request in
       * flight cannot support. It is the same distinction the check cards draw between `loading` and
       * `unavailable`, and it belongs here for the same reason.
       *
       * It also holds the field's footprint. Without it the details card renders five fields and
       * then grows a sixth when the permission lands, so the card visibly reflows on every load.
       */
      status: 'loading';
    };

/**
 * Renders the permission as the Details card's sixth field: caption, status word, date.
 *
 * The status word is the field's value and is always literally "Active" or "Inactive". The date
 * line beneath it swaps label entirely, so a revoked permission shows only its revocation date.
 * Showing both dates would leave the reader working out which one is current.
 *
 * **Inactive is a deliberate state, not a disabled one**, so it keeps full strength text rather
 * than greying the block out. Greying it out made a revoked permission read as merely low contrast,
 * and it is the more consequential of the two states.
 *
 * **The icon is decorative.** The status word carries the meaning on its own, so colour is never
 * the sole signal and the icon takes `aria-hidden` rather than a `titleAccess` label that would
 * announce the state a second time. Tests locate this component by its text, not by an icon name.
 *
 * **Takes no `data-testid`.** It is one field inside the Details card, which carries the testid the
 * page keeps unique. Per ADR 72 it is located from the card.
 */
export default function PermissionBlock(props: Readonly<TPermissionBlockProps>): ReactElement {
  // A guard clause rather than a ternary per line. The caption is kept and only the status word and
  // the date are replaced, so the block holds its height and the card does not resize as the
  // permission lands.
  if (props.status === 'loading') {
    return (
      <Stack aria-busy sx={(t: Theme) => ({ gap: t.semantic.spacing['3xs'] })}>
        <Typography variant="caption" color="text.secondary">
          Permission
        </Typography>
        {/* Sized to the two lines the ready state renders: the status word, then its date. */}
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="70%" />
      </Stack>
    );
  }

  if (props.status === 'none') {
    return (
      <Stack sx={(t: Theme) => ({ gap: t.semantic.spacing['3xs'] })}>
        <Typography variant="caption" color="text.secondary">
          Permission
        </Typography>
        {/* No icon and no second line. The two-line shape belongs to a permission that exists and
            has a date; there is no date here and no state to signal. */}
        <Typography variant="body1">{MISSING_VALUE}</Typography>
      </Stack>
    );
  }

  // Read the discriminant through `props` rather than destructuring it: a destructured copy is
  // disconnected from the union and TypeScript can no longer tell which date field exists.
  const isActive = props.status === 'active';
  const StatusIcon = isActive ? CheckCircleIcon : DoNotDisturbOnIcon;
  const dateLine =
    props.status === 'active' ? `Granted on ${props.grantedOn}` : `Revoked on ${props.revokedOn}`;

  return (
    <Stack sx={(t: Theme) => ({ gap: t.semantic.spacing['3xs'] })}>
      <Typography variant="caption" color="text.secondary">
        Permission
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        sx={(t: Theme) => ({
          gap: t.semantic.spacing['2xs'],
          // Set once on the row so the icon inherits it through `currentColor`. These are the
          // on-surface text tones, not `success.main` / `error.main`, which are fill colours and
          // fall below AA against the dark theme's paper.
          color: isActive ? 'text.success' : 'text.danger',
        })}
      >
        <StatusIcon fontSize="small" aria-hidden sx={{ flex: 'none' }} />
        <Typography
          variant="body1"
          sx={(t: Theme) => ({ fontWeight: t.semantic.fontWeight.semibold })}
        >
          {isActive ? 'Active' : 'Inactive'}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {dateLine}
      </Typography>
    </Stack>
  );
}
